import { useState, useEffect } from 'react';
import { Card, CardContent } from '../core/Card';
import { useActiveUsers } from '../../graphql/hooks/useUserDashboard';
import { PayrollView } from './PayrollView';
import { RequestsAdminViewTab } from './RequestsAdminViewTab';
import { MonthClosureView } from './MonthClosureView';
import { UserManagementView } from './UserManagementView';
import { AdminDashboardView } from './AdminDashboardView';
import { HolidaysView } from './HolidaysView';
import { SickLeaveView } from './SickLeaveView';
import { DocumentsView } from './DocumentsView';
import { DatevView } from './DatevView';
import { EmergencyServiceRateView } from './EmergencyServiceRateView';
import type { RequestsPreset } from './requestFilters';

type AdminTab = 'dashboard' | 'sick-leave' | 'payroll' | 'requests' | 'month-closure' | 'user-management' | 'vehicles' | 'holidays' | 'documents' | 'datev' | 'emergency-service-rate';

export function AdminUserView() {
  const [activeTab, setActiveTab] = useState<AdminTab>('dashboard');
  // filters for the Anträge tab when it is opened from another page
  const [requestsPreset, setRequestsPreset] = useState<RequestsPreset | null>(null);

  const openTab = (tab: AdminTab) => {
    setRequestsPreset(null);
    setActiveTab(tab);
  };

  const openRequestsWithPreset = (preset: RequestsPreset) => {
    setRequestsPreset(preset);
    setActiveTab('requests');
  };

  const [openHolidayDropdownId, setOpenHolidayDropdownId] = useState<string | null>(null);

  const { data: users } = useActiveUsers();

  const [userId, setUserId] = useState<string>("");

  useEffect(() => {
    if (users?.length && !userId) {
      setUserId(users[0].id);
    }
  }, [users, userId]);

  useEffect(() => {
    const handleClickOutside = () => {
      setOpenHolidayDropdownId(null);
    };

    if (openHolidayDropdownId) {
      document.addEventListener('click', handleClickOutside);
    }

    return () => {
      document.removeEventListener('click', handleClickOutside);
    };
  }, [openHolidayDropdownId]);

  const tabs = [
    { id: 'dashboard' as AdminTab, label: 'Dashboard' },
    { id: 'payroll' as AdminTab, label: 'Lohnbüro' },
    { id: 'requests' as AdminTab, label: 'Anträge' },
    { id: 'month-closure' as AdminTab, label: 'Monatsabschlüsse' },
    { id: 'user-management' as AdminTab, label: 'Benutzer' },
    { id: 'holidays' as AdminTab, label: 'Feiertage' },
    { id: 'sick-leave' as AdminTab, label: 'Krankmeldungen' },
    { id: 'documents' as AdminTab, label: 'Dokumente' },
    { id: 'datev' as AdminTab, label: 'DATEV' },
    { id: 'emergency-service-rate' as AdminTab, label: 'Konfiguration' },
  ];

  return (
    <div className="bg-[#f5f5f5] min-h-screen p-4 md:p-8">
      <div className="max-w-[1400px] mx-auto">
        {/* Header */}
        <div className="mb-6">
          {/* Tabs */}
          <div className="flex flex-wrap gap-2 border-b border-[#e0e0e0] pb-2">
            {tabs.map((tab) => (
              <button
                key={tab.id}
                onClick={() => openTab(tab.id)}
                className={`px-4 py-2 text-[16px] transition-colors relative ${
                  activeTab === tab.id
                    ? 'text-[#0084c7] font-medium'
                    : 'text-[#6b6b6b] hover:text-[#1c1c1c]'
                }`}
              >
                {tab.label}
                {activeTab === tab.id && (
                  <div className="absolute bottom-[-10px] left-0 right-0 h-[2px] bg-[#0084c7]" />
                )}
              </button>
            ))}
          </div>
        </div>

        {/* Content Area */}
        <Card>
          <CardContent>
            {activeTab === 'sick-leave' && (
              <SickLeaveView />     
            )}

            {activeTab === 'payroll' && (
              <PayrollView />
            )}

            {activeTab === 'requests' && (
              <RequestsAdminViewTab preset={requestsPreset} />
            )}

            {activeTab === 'month-closure' && (
              <MonthClosureView onOpenRequests={openRequestsWithPreset} />
            )}

            {activeTab === 'user-management' && (
              <UserManagementView />
            )}

            {activeTab === 'dashboard' && (
              <AdminDashboardView />
            )}
            {activeTab === 'holidays' && (
              <HolidaysView />
            )}
            {activeTab === 'documents' && users && (
              <DocumentsView users={users} />
            )}
            {activeTab === 'datev' && (
              <DatevView />
            )}
            {activeTab === 'emergency-service-rate' && (
              <EmergencyServiceRateView />
            )}
          </CardContent>
        </Card>

        {/* Footer */}
        <div className="text-center my-4">
          <p className="text-[#9ca3af] text-[12px]">
            © 2026 ENT-TRO GmbH by{' '}
            <a
              href="https://curvedesign.de"
              target="_blank"
              rel="noopener noreferrer"
              className="underline text-inherit"
            >
              CurveDesign GmbH
            </a>
          </p>
        </div>
      </div>
    </div>
  );
}